import React, { useMemo, useState } from 'react';
import { ContentItem } from '@/components/M_Molecules/M_ContentCard/M_ContentCard';
import M_ContentCard from '@/components/M_Molecules/M_ContentCard/M_ContentCard';
import { W_MasonryGrid } from '@/components/W_Wrappers/W_MasonryGrid/W_MasonryGrid';
import O_CapsulePost from '@/components/O_Organisms/O_CapsulePost/O_CapsulePost';
import O_CapsuleLayout from '@/components/O_Organisms/O_CapsuleLayout/O_CapsuleLayout';
import styles from './CapsuleCreatePreview.module.css';
import { Capsule } from '@/services/capsulesApi';
import O_CapsulePreviewHeader from '@/components/O_Organisms/O_CapsulePreviewHeader/O_CapsulePreviewHeader';
import { CapsuleReactionsView } from '@/components/capsule-view/CapsuleReactionsView';
import M_SelectPicker from '@/components/M_Molecules/M_SelectPicker/M_SelectPicker';
import cn from 'classnames';
import { useTranslation } from '@/hooks/useTranslation';
import {
  CapsuleViewMode,
  defaultCapsuleView,
  getCapsuleViewOptions,
} from '@/components/capsule-view/constants';
import W_CapsuleContentEmptyState from '@/components/W_Wrappers/W_CapsuleContentEmptyState/W_CapsuleContentEmptyState';

interface CapsuleCreatePreviewProps {
  content: ContentItem[];
  capsule: Capsule;
  onExit?: () => void;
  onSaveContent: (contentItem: any) => void;
  onPublish?: () => void;
}

export default function CapsuleCreatePreview({
  content,
  capsule,
  onExit,
  onSaveContent,
  onPublish,
}: CapsuleCreatePreviewProps) {
  const t = useTranslation();
  const [isPostExpanded, setIsPostExpanded] = useState(true);
  const [selectedView, setSelectedView] =
    useState<CapsuleViewMode>(defaultCapsuleView);

  const viewOptions = useMemo(() => getCapsuleViewOptions(t), [t]);

  const togglePostExpanded = () => setIsPostExpanded(prev => !prev);

  const handleViewChange = (view: CapsuleViewMode) => {
    setSelectedView(view);
  };

  const handleViewSelect = (option: string) => {
    const nextView = viewOptions.find(({ label }) => label === option)?.key;
    if (nextView) {
      setSelectedView(nextView);
    }
  };

  const postText = useMemo(() => {
    const textItems = (content || [])
      .filter(
        (it: any) => it.type === 'text' && typeof (it as any).text === 'string'
      )
      .map((it: any) => (it as any).text as string);
    return capsule.description || textItems.join('\n');
  }, [capsule.description, content]);

  const capsuleForReactions = useMemo(() => {
    if (!capsule.scenario) return capsule;
    return {
      ...capsule,
      scenario: {
        ...capsule.scenario,
        comments: [],
      },
    };
  }, [capsule]);

  const capsulePostData = useMemo(() => {
    const baseAuthorAvatar = capsule.author?.avatar || '';
    const coauthorAvatars =
      capsule.privacy?.coauthors?.map(
        coauthor => coauthor.avatar || baseAuthorAvatar
      ) || [];
    const allAvatars = [baseAuthorAvatar, ...coauthorAvatars].filter(Boolean);

    return {
      id: capsule.id,
      title: capsule.title || '',
      content: postText,
      author: capsule.author?.name || '',
      authorAvatar: baseAuthorAvatar,
      timestamp: capsule.meta?.createdAt || '',
      likes: 0,
      comments: capsule.engagement?.comments || 0,
      avatars: allAvatars,
      coauthors: capsule.privacy?.coauthors?.length || 0,
      isMyCapsule: true,
      ownerName: t('capsuleCreate.authorName'),
      ownerId: capsule.author?.id || '',
      createdAt: capsule.meta?.createdAt || '',
      firstOpenedAt: capsule.meta?.firstOpenedAt || '',
      treeId: capsule.tree?.id,
      capsulePostAuthorDate: {
        avatars: allAvatars,
        coauthors: capsule.privacy?.coauthors?.length || 0,
        date: capsule.meta?.createdAt || '',
        isMyCapsule: true,
        ownerName: t('capsuleCreate.authorName'),
        ownerId: capsule.author?.id || '',
      },
      capsulePostContent: {
        title: capsule.title || '',
        text: postText,
        commentCount: capsule.engagement?.comments || 0,
        plantCount: 0,
        indicator: 'comment' as const,
      },
    };
  }, [capsule, postText, t]);

  const renderPreviewHeader = (className?: string) => (
    <div className={cn(styles.header_wrapper, className)}>
      <O_CapsulePreviewHeader
        onToggleExpanded={togglePostExpanded}
        isExpanded={isPostExpanded}
        onExit={onExit}
        onPublish={onPublish}
        showExpandButton
      />
      {!isPostExpanded && (
        <O_CapsuleLayout
          title={capsule.title || ''}
          commentCount={capsule.engagement.comments || 0}
          plantCount={0}
        />
      )}
    </div>
  );

  const renderViewSwitcher = (className?: string) => (
    <div className={cn(styles.bottom_wrapper, className)}>
      <div className={cn(styles.select_picker_wrapper, 'effect-green-glow')}>
        <M_SelectPicker
          options={viewOptions.map(({ label }) => label)}
          selectedOption={
            viewOptions.find(({ key }) => key === selectedView)?.label ||
            viewOptions[0]?.label ||
            ''
          }
          onSelect={handleViewSelect}
          background='white'
        />
      </div>
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.content_wrapper}>
        {selectedView !== 'reactions' && renderPreviewHeader()}
        {selectedView === 'content' ? (
          <>
            {content.length > 0 ? (
              <W_MasonryGrid>
                {content.map(item => (
                  <div key={item.id}>
                    <M_ContentCard content={item} />
                  </div>
                ))}
              </W_MasonryGrid>
            ) : (
              <W_CapsuleContentEmptyState onSaveContent={onSaveContent} />
            )}
            {renderViewSwitcher()}
          </>
        ) : (
          <CapsuleReactionsView
            capsule={capsuleForReactions}
            isFullscreen={false}
            selectedView={selectedView}
            onViewSelect={handleViewChange}
            mode='preview'
            backgroundMode='tunnel'
            headerSlot={
              <O_CapsulePreviewHeader
                onToggleExpanded={() => setSelectedView('content')}
                isExpanded={isPostExpanded}
                onExit={onExit}
                onPublish={onPublish}
                showExpandButton={false}
              />
            }
          />
        )}
        {selectedView === 'reactions' &&
          renderViewSwitcher(styles.reactions_select_wrapper)}
      </div>
      {selectedView === 'content' && isPostExpanded && (
        <div className={styles.post_wrapper}>
          <O_CapsulePost capsule={capsulePostData as any} />
        </div>
      )}
    </div>
  );
}
